import { useRef, useEffect } from 'react';
import { GlassPanel } from '@/components/GlassPanel';
import { ShieldCheck, Heart, Award, UserCheck } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

const BrandGuardianDiagram = ({ diagramLabels, diagramSub }) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const st = { trigger: wrapRef.current, start: 'top 82%' };

      // Obroči se pojavijo od zunaj navznoter
      ['#bgf-r1','#bgf-r2','#bgf-r3','#bgf-r4'].forEach((id, i) => {
        gsap.fromTo(id,
          { opacity: 0, scale: 0.6, transformOrigin: '150px 150px' },
          { opacity: 1, scale: 1, duration: 0.7, ease: 'power3.out', delay: i * 0.15,
            scrollTrigger: st }
        );
      });

      // Center se pojavi zadnji
      gsap.fromTo('#bgf-center-circle',
        { opacity: 0, scale: 0.5, transformOrigin: '150px 150px' },
        { opacity: 1, scale: 1, duration: 0.6, ease: 'back.out(2)', delay: 0.7,
          scrollTrigger: st }
      );

      // Labele
      gsap.fromTo('.bgf-lbl',
        { opacity: 0 },
        { opacity: 1, duration: 0.5, stagger: 0.08, delay: 0.9,
          scrollTrigger: st }
      );

      // Pulse na center - odstranjen
      // Center pulse je statičen
    }, wrapRef);
    return () => ctx.revert();
  }, []);

  const CX = 150, CY = 150;

  return (
    <div ref={wrapRef} className="relative select-none">
      <div className="text-center mb-4">
        <span className="font-mono text-[11px] text-[#C87533]/70 uppercase tracking-[0.2em]">
          Brand Guardian Framework
        </span>
      </div>

      <svg viewBox="0 0 300 300" className="w-full max-w-[300px] mx-auto block">
        <defs>
          <radialGradient id="bgfBg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C87533" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#C87533" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Ambient glow */}
        <circle cx={CX} cy={CY} r="110" fill="url(#bgfBg)" />

        {/* Ring 1 – Compliance (outer) */}
        <circle id="bgf-r1" cx={CX} cy={CY} r="128"
          fill="none" stroke="rgba(255,255,255,0.13)" strokeWidth="1" />

        {/* Ring 2 – Brand Values */}
        <circle id="bgf-r2" cx={CX} cy={CY} r="98"
          fill="rgba(200,117,51,0.04)" stroke="rgba(200,117,51,0.3)" strokeWidth="1.2" />

        {/* Ring 3 – Informator */}
        <circle id="bgf-r3" cx={CX} cy={CY} r="68"
          fill="rgba(200,117,51,0.08)" stroke="rgba(200,117,51,0.5)" strokeWidth="1.5" />

        {/* Ring 4 – inner fill */}
        <circle id="bgf-r4" cx={CX} cy={CY} r="42"
          fill="rgba(200,117,51,0.18)" stroke="rgba(200,117,51,0.75)" strokeWidth="2" />

        {/* Center */}
        <circle id="bgf-center-circle" cx={CX} cy={CY} r="28"
          fill="rgba(200,117,51,0.3)" stroke="rgba(200,117,51,0.9)" strokeWidth="2" />

        {/* ── Labels ── */}
        {/* COMPLIANCE – med r1 in r2 zgoraj */}
        <text className="bgf-lbl" x={CX} y="14" textAnchor="middle"
          fontFamily="monospace" fontSize="7.5" fill="rgba(255,255,255,0.38)" letterSpacing="2">
          COMPLIANCE
        </text>
        <line className="bgf-lbl" x1={CX} y1="18" x2={CX} y2="22"
          stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" />

        {/* BRAND VALUES – med r2 in r3 zgoraj */}
        <text className="bgf-lbl" x={CX} y="46" textAnchor="middle"
          fontFamily="monospace" fontSize="7" fill="rgba(200,117,51,0.6)" letterSpacing="1.5">
          BRAND VALUES
        </text>
        <line className="bgf-lbl" x1={CX} y1="50" x2={CX} y2="82"
          stroke="rgba(200,117,51,0.2)" strokeWidth="0.5" />

        {/* INFORMATOR – vrh med r3 in r4 */}
        <text className="bgf-lbl" x={CX} y="88" textAnchor="middle"
          fontFamily="monospace" fontSize="8" fill="rgba(200,117,51,0.9)" letterSpacing="2" fontWeight="bold">
          INFORMATOR
        </text>

        {/* KAKOVOST – spodaj */}
        <line className="bgf-lbl" x1={CX} y1="278" x2={CX} y2="258"
          stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
        <text className="bgf-lbl" x={CX} y="287" textAnchor="middle"
          fontFamily="monospace" fontSize="7.5" fill="rgba(255,255,255,0.35)" letterSpacing="2">
          {diagramLabels.quality}
        </text>

        {/* RSP – levo */}
        <text className="bgf-lbl" x="14" y={CY + 4} textAnchor="middle"
          fontFamily="monospace" fontSize="7" fill="rgba(255,255,255,0.32)" letterSpacing="1.5"
          transform={`rotate(-90 14 ${CY})`}>RSP</text>

        {/* ETIKA – desno */}
        <text className="bgf-lbl" x="286" y={CY + 4} textAnchor="middle"
          fontFamily="monospace" fontSize="7" fill="rgba(255,255,255,0.32)" letterSpacing="1.5"
          transform={`rotate(90 286 ${CY})`}>{diagramLabels.ethics}</text>
      </svg>

      {/* Center ShieldCheck icon – positioned over SVG center */}
      <div className="bgf-lbl absolute"
        style={{ top: '50%', left: '50%', transform: 'translate(-50%, -44%)' }}>
        <ShieldCheck className="w-6 h-6 text-[#C87533]"
          style={{ filter: 'drop-shadow(0 0 6px rgba(200,117,51,0.8))' }} />
      </div>

      <p className="text-center text-[10px] text-white/25 font-mono mt-3 tracking-wider uppercase">
        {diagramSub}
      </p>
    </div>
  );
};

export const MarketingSection = () => {
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const tx = t(lang).marketing;
  const ICONS = [ShieldCheck, Heart, Award, UserCheck];

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      gsap.fromTo('.mkt-item',
        { opacity: 0, x: -20 },
        { opacity: 1, x: 0, duration: 0.6, ease: 'power3.out', stagger: 0.12,
          scrollTrigger: { trigger: sectionRef.current, start: 'top 78%' } }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="marketing"
      ref={sectionRef}
      data-testid="marketing-section"
      className="scroll-section min-h-screen flex items-center px-4"
    >
      <div className="section-content max-w-[1200px] mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-8 h-px bg-[#C87533]" />
          <span className="font-mono text-xs text-[#C87533] uppercase tracking-wider">{tx.label}</span>
        </div>

        <div className="grid gap-10 lg:grid-cols-2 items-center">
          {/* Left: Content */}
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-4 text-white">
              {tx.headline}
            </h2>
            <p className="text-sm sm:text-base text-white/65 leading-relaxed mb-8">
              {tx.bodyBase}
              <span className="text-[#C87533] font-semibold"> {tx.bodyAccent} </span>
              {tx.bodyEnd}
            </p>

            <div className="space-y-4">
              {tx.pillars.map((item, i) => {
                const Icon = ICONS[i];
                return (
                  <div key={i} className="mkt-item flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-xl bg-[#C87533]/15 flex items-center justify-center flex-shrink-0 border border-[#C87533]/20">
                      <Icon className="w-5 h-5 text-[#C87533]" />
                    </div>
                    <div>
                      <h3 className="font-display text-base font-semibold text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-white/60 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Diagram */}
          <div>
            <GlassPanel variant="strong" className="p-6 sm:p-8 relative overflow-hidden">
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(200,117,51,0.05) 0%, transparent 70%)' }} />
              <BrandGuardianDiagram diagramLabels={tx.diagramLabels} diagramSub={tx.diagramSub} />
            </GlassPanel>
          </div>
        </div>
      </div>
    </section>
  );
};
